/**
 * <cardza-card> — turns a flat .cz-card into a lit physical object.
 *
 * Writes four custom properties that assets/cardza.css consumes:
 *   --cz-rx / --cz-ry   tilt, in degrees
 *   --cz-mx / --cz-my   pointer position, drives the specular sweep
 *   --cz-lift           0..1 proximity value, drives highlight + scale
 *
 * Deliberately standalone: no dependency on Horizon's module graph, so a
 * theme update can't break it. With JS off or reduced-motion on, the card
 * stays a statically lit object and nothing here runs.
 *
 * Attributes:
 *   max-tilt   degrees of rotation at the edges (default 9)
 *   flip       present = click/Enter flips to the reverse face
 */

const REDUCED = matchMedia('(prefers-reduced-motion: reduce)');
const FINE_POINTER = matchMedia('(hover: hover) and (pointer: fine)');

class Cards4UCard extends HTMLElement {
  #card = null;
  #frame = 0;
  #pending = null;

  connectedCallback() {
    this.#card = this.querySelector('.cz-card');
    if (!this.#card) return;

    if (this.hasAttribute('flip')) {
      this.tabIndex = 0;
      this.setAttribute('role', 'button');
      this.setAttribute('aria-pressed', 'false');
      this.addEventListener('click', this.#toggleFlip);
      this.addEventListener('keydown', this.#onKey);
    }

    // Tilt is a pointer affordance. Skip it entirely on touch and for anyone
    // who asked for less motion — the CSS fallback already looks intentional.
    if (REDUCED.matches || !FINE_POINTER.matches) return;

    this.addEventListener('pointermove', this.#onMove, { passive: true });
    this.addEventListener('pointerenter', this.#onEnter, { passive: true });
    this.addEventListener('pointerleave', this.#onLeave, { passive: true });
  }

  disconnectedCallback() {
    cancelAnimationFrame(this.#frame);
    this.removeEventListener('pointermove', this.#onMove);
    this.removeEventListener('pointerenter', this.#onEnter);
    this.removeEventListener('pointerleave', this.#onLeave);
    this.removeEventListener('click', this.#toggleFlip);
    this.removeEventListener('keydown', this.#onKey);
  }

  get #maxTilt() {
    const raw = Number.parseFloat(this.getAttribute('max-tilt'));
    return Number.isFinite(raw) ? raw : 9;
  }

  #onEnter = () => {
    this.dataset.active = '';
  };

  #onMove = (event) => {
    // Cache the coordinates and coalesce to one write per frame; pointermove
    // fires far faster than we can usefully paint.
    this.#pending = { x: event.clientX, y: event.clientY };
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(this.#apply);
  };

  #apply = () => {
    this.#frame = 0;
    const point = this.#pending;
    if (!point || !this.#card) return;

    const box = this.getBoundingClientRect();
    if (!box.width || !box.height) return;

    // Normalised -0.5..0.5 from the card's centre.
    const nx = (point.x - box.left) / box.width - 0.5;
    const ny = (point.y - box.top) / box.height - 0.5;

    const tilt = this.#maxTilt;
    const style = this.#card.style;

    // Y-axis follows horizontal travel, X-axis inverts so the card leans
    // *toward* the cursor rather than away from it.
    style.setProperty('--cz-ry', `${(nx * tilt * 2).toFixed(2)}deg`);
    style.setProperty('--cz-rx', `${(-ny * tilt * 2).toFixed(2)}deg`);
    style.setProperty('--cz-mx', `${((nx + 0.5) * 100).toFixed(1)}%`);
    style.setProperty('--cz-my', `${((ny + 0.5) * 100).toFixed(1)}%`);
    style.setProperty('--cz-lift', '1');
  };

  #onLeave = () => {
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
    this.#pending = null;
    delete this.dataset.active;

    if (!this.#card) return;
    const style = this.#card.style;
    style.setProperty('--cz-rx', '0deg');
    style.setProperty('--cz-ry', '0deg');
    style.setProperty('--cz-mx', '50%');
    style.setProperty('--cz-my', '30%');
    style.setProperty('--cz-lift', '0');
  };

  #onKey = (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    this.#toggleFlip();
  };

  #toggleFlip = () => {
    const flipped = this.hasAttribute('data-flipped');
    this.toggleAttribute('data-flipped', !flipped);
    this.setAttribute('aria-pressed', String(!flipped));
  };
}

if (!customElements.get('cardza-card')) {
  customElements.define('cardza-card', Cards4UCard);
}
